import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, Plus, RefreshCw, Trash2, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const PRESET_COLORS = ["#0ea5e9", "#a855f7", "#ef4444", "#f59e0b", "#10b981", "#6366f1"];

function HelpInstructions() {
  return (
    <div className="text-[12px] leading-relaxed text-muted-foreground space-y-1.5">
      <p className="font-semibold text-foreground">Get your Outlook ICS URL</p>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Open <a className="text-primary underline" href="https://outlook.office.com/calendar/options/calendar/SharedCalendars" target="_blank" rel="noreferrer">Outlook → Settings → Shared calendars</a> (use your work account).</li>
        <li>Under <em>Publish a calendar</em>, choose your calendar and <strong>Can view all details</strong>.</li>
        <li>Click <em>Publish</em>, then copy the <strong>ICS</strong> link (ends with <code>.ics</code>).</li>
        <li>Paste it here. RENVIRO syncs it every 30 min — read-only.</li>
      </ol>
      <p className="text-[11px] mt-2">Working from Outlook desktop? <a className="text-primary underline" href="https://support.microsoft.com/en-us/office/share-a-calendar-with-others-353eba5c-b15a-4b78-a883-d5fe61b14c08" target="_blank" rel="noreferrer">Microsoft's full publish-calendar guide →</a></p>
    </div>
  );
}

export default function CalendarFeedsManager() {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", url: "", color: PRESET_COLORS[0] });
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/feeds");
      setFeeds(data);
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll to this section + open the dialog when ?tab=calendar
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "calendar") {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="calendar-feeds-card"]');
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(true);
      }, 250);
    }
  }, []);

  const add = async () => {
    if (!form.label.trim() || !form.url.trim()) { toast.error("Label and URL required"); return; }
    setSaving(true);
    try {
      await api.post("/calendar/feeds", form);
      toast.success("Calendar connected");
      setForm({ label: "", url: "", color: PRESET_COLORS[0] });
      setOpen(false);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  const sync = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/calendar/feeds/${id}/sync`);
      if (data.result?.ok) toast.success(`Synced — ${data.result.count} events`);
      else toast.error(`Sync failed: ${data.result?.error || "unknown"}`);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setSyncingId(null); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Disconnect "${label}"? Its events will be removed from your calendar.`)) return;
    try {
      await api.delete(`/calendar/feeds/${id}`);
      toast.success("Calendar disconnected");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mt-5" data-testid="calendar-feeds-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground flex items-center gap-1.5">
              <CalendarClock className="h-3 w-3" /> Connected calendars
            </p>
            <p className="text-xs text-muted-foreground mt-1">Subscribe to your Microsoft Outlook calendar via ICS — meetings booked in Outlook appear in your RENVIRO calendar view.</p>
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-2xl tap" size="sm" data-testid="add-calendar-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> Connect
          </Button>
        </div>

        {loading ? <Skeleton className="h-24" /> : feeds.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No Outlook calendar connected yet. Connect one to see all your meetings in a single calendar view.
          </div>
        ) : (
          <div className="space-y-2">
            {feeds.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-3 rounded-2xl border border-border/60" data-testid={`feed-${f.id}`}>
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: f.color || "#0ea5e9" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2">
                {f.label}
                {f.last_error ? (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] uppercase tracking-wider"><AlertCircle className="h-3 w-3 mr-1" />{f.last_error}</Badge>
                ) : (
                  <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] uppercase tracking-wider"><CheckCircle2 className="h-3 w-3 mr-1" />Outlook · {f.event_count || 0} events</Badge>
                )}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {f.last_sync_at ? `Last synced ${fmtDateTime(f.last_sync_at)}` : "Never synced"}
                  </p>
                </div>
                <Button variant="outline" size="icon" className="rounded-xl tap" onClick={() => sync(f.id)} disabled={syncingId === f.id} data-testid={`sync-feed-${f.id}`}>
                  <RefreshCw className={`h-3.5 w-3.5 ${syncingId === f.id ? "animate-spin" : ""}`} />
                </Button>
                <Button variant="outline" size="icon" className="rounded-xl tap text-red-700 hover:bg-red-50" onClick={() => remove(f.id, f.label)} data-testid={`remove-feed-${f.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-calendar-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Connect Outlook calendar</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">Paste the ICS URL from Microsoft Outlook (Settings → Shared calendars → Publish). Read-only — RENVIRO never modifies your Outlook calendar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Label</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="My Outlook calendar" data-testid="feed-label-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">ICS URL</Label>
              <Input className="rounded-2xl h-11 mt-1.5 font-mono text-[12px]" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://outlook.office365.com/owa/calendar/.../calendar.ics" data-testid="feed-url-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Color</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`h-8 w-8 rounded-full border-2 tap ${form.color === c ? "border-foreground" : "border-transparent"}`} style={{ background: c }} aria-label={c} />
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border/60 p-3 bg-muted/30">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 flex items-center gap-1"><ExternalLink className="h-3 w-3" /> How to find your ICS link</p>
              <HelpInstructions />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} disabled={saving} className="rounded-2xl tap" data-testid="feed-save-button">
              {saving ? "Validating…" : "Connect & sync"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
